import React from 'react';
import { Navigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { colors } from '@/constants/colors';
import { useSurfaceStore } from '@/store/useSurfaceStore';
import { useAutoDetectSurface } from '@/hooks/useAutoDetectSurface';
import { takeSeller } from '@/utils/sellerReturn';
import { hasSetupDraft } from '@/utils/setupDraft';

/**
 * Post-login landing route. Picks the right surface dashboard so users never
 * see a generic /admin or bounce through a redirect on every login.
 *
 * Order of precedence:
 *   1. Last manual choice in localStorage (handled inside useSurfaceStore on init).
 *   2. Auto-detect: main vendo → wifi; only stores → voucher-store; else wifi.
 *
 * Once the store reports `resolved: true`, we navigate to the surface's home.
 */
const SurfaceRedirect = () => {
   useAutoDetectSurface();
   const { surface, resolved } = useSurfaceStore();

   // A shopper who pressed Buy Now on a seller page while signed out was sent
   // here to sign in. Send them back to that seller instead of the dashboard,
   // or the sign-in detour loses the seller all over again. Read once via a
   // lazy initialiser — takeSeller() clears the note, and this component
   // re-renders while the surface resolves.
   const [pendingSeller] = React.useState(() => takeSeller());
   if (pendingSeller) {
      return <Navigate to={`/s/${encodeURIComponent(pendingSeller)}`} replace />;
   }

   if (!resolved) {
      return (
         <Center>
            <Spinner />
         </Center>
      );
   }

   // A setup begun on smartfi.ph is about a vendo, whatever surface this
   // account last used — the device list is where the wizard reopens.
   // A pending smartfi.ph setup goes to the device list, where the register
   // wizard opens (it lives on the devices page, not the WiFi overview).
   const target = hasSetupDraft() ? '/wifi-devices' : surface === 'voucher-store' ? '/voucher' : '/wifi';
   return <Navigate to={target} replace />;
};

export default SurfaceRedirect;

const spin = keyframes`
   to { transform: rotate(360deg); }
`;

const Center = styled.div`
   display: flex;
   align-items: center;
   justify-content: center;
   height: 100vh;
   background: var(--app-surface);
`;

const Spinner = styled.div`
   width: 36px;
   height: 36px;
   border-radius: 50%;
   border: 3px solid ${colors.GRAY14};
   border-top-color: ${colors.PRIMARY};
   animation: ${spin} 0.8s linear infinite;
`;
